<template>
    <div class="flex items-end w-full gap-3 lg:col-span-1">
        <!-- Guest picker -->
        <div class="relative flex-1">
            <label v-if="label" class="filter-label">{{ label }}</label>

            <button
                type="button"
                class="flex items-center justify-between w-full field-sm"
                :aria-expanded="isOpen.toString()"
                :aria-controls="panelId"
                @click="toggle"
                @keydown.enter.prevent="toggle"
                @keydown.space.prevent="toggle"
            >
                <span class="inline-flex items-center gap-2">
                    <AppIcon icon="mdi:person" class="w-4 h-4 text-gray-600" />
                    <span class="truncate">{{ guestText }}</span>
                </span>
                <AppIcon
                    icon="mdi:chevron-down"
                    class="w-4 h-4 text-gray-500"
                />
            </button>

            <!-- Popover -->
            <transition name="fade">
                <div
                    v-if="isOpen"
                    :id="panelId"
                    class="absolute z-50 mt-2 w-[240px] rounded-xl border border-gray-100 bg-white shadow-xl p-3 right-0"
                    @click.stop
                >
                    <div class="flex items-center justify-between">
                        <span class="text-sm text-gray-700">Guests</span>
                        <div class="inline-flex items-center gap-2">
                            <button
                                type="button"
                                class="qty-btn"
                                @click="dec"
                                :disabled="valueLocal.guests <= min"
                                aria-label="Decrease guests"
                            >
                                −
                            </button>
                            <span
                                class="w-6 text-sm text-center tabular-nums"
                                >{{ valueLocal.guests }}</span
                            >
                            <button
                                type="button"
                                class="qty-btn"
                                @click="inc"
                                :disabled="valueLocal.guests >= max"
                                aria-label="Increase guests"
                            >
                                ＋
                            </button>
                        </div>
                    </div>

                    <div class="flex items-center justify-between mt-3">
                        <button
                            type="button"
                            class="text-xs text-gray-500 hover:text-gray-700"
                            @click="reset"
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            class="btn-apply"
                            @click="applyAndClose"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            </transition>
        </div>

        <!-- Search button -->
        <button type="button" class="btn-search-sm" @click="$emit('search')">
            <i class="text-sm fa-solid fa-magnifying-glass"></i>
            <span>Search</span>
        </button>
    </div>
</template>

<script>
export default {
    name: "BaseGuestSearch",
    emits: ["update:modelValue", "search"],
    props: {
        modelValue: {
            type: Object,
            default: () => ({ guests: 1 }),
        },
        label: { type: String, default: "Who" },
        min: { type: Number, default: 1 },
        max: { type: Number, default: 12 },
    },
    data() {
        return {
            isOpen: false,
            valueLocal: { guests: 1 },
            panelId: `guest-pop-${Math.random().toString(36).slice(2, 9)}`,
        };
    },
    computed: {
        guestText() {
            const g = Number(this.valueLocal.guests || 1);
            return `${g} Guest${g > 1 ? "s" : ""}`;
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            deep: true,
            handler(v) {
                const g = Number(v?.guests ?? 1);
                this.valueLocal.guests = this.clamp(g);
            },
        },
    },
    mounted() {
        document.addEventListener("click", this.onDocClick);
        document.addEventListener("keydown", this.onKeydown);
    },
    beforeUnmount() {
        document.removeEventListener("click", this.onDocClick);
        document.removeEventListener("keydown", this.onKeydown);
    },
    methods: {
        clamp(n) {
            if (Number.isNaN(n)) return this.min;
            return Math.min(this.max, Math.max(this.min, n));
        },
        toggle() {
            this.isOpen = !this.isOpen;
        },
        onDocClick(e) {
            if (this.isOpen && !this.$el.contains(e.target))
                this.isOpen = false;
        },
        onKeydown(e) {
            if (this.isOpen && e.key === "Escape") this.isOpen = false;
        },
        inc() {
            this.valueLocal.guests = this.clamp(this.valueLocal.guests + 1);
        },
        dec() {
            this.valueLocal.guests = this.clamp(this.valueLocal.guests - 1);
        },
        reset() {
            this.valueLocal.guests = this.min;
        },
        applyAndClose() {
            this.$emit("update:modelValue", { guests: this.valueLocal.guests });
            this.isOpen = false;
        },
    },
};
</script>

<style scoped>
/* kecil dan proporsional dengan field lain */
.field-sm {
    @apply h-10 px-3 rounded-lg border border-gray-300 bg-white text-sm;
}
/* label kecil seperti komponen lain */
.filter-label {
    @apply block mb-1 font-medium text-[11px] text-gray-600;
}
/* tombol search kecil */
.btn-search-sm {
    @apply h-10 px-4 rounded-xl bg-primary text-white text-sm font-medium inline-flex items-center gap-2 whitespace-nowrap;
}
/* tombol + / - */
.qty-btn {
    @apply w-7 h-7 rounded-md border border-gray-300 text-gray-700 leading-[1] flex items-center justify-center hover:bg-gray-50 disabled:opacity-40;
}
/* apply button di popover */
.btn-apply {
    @apply h-8 px-3 rounded-lg bg-blue-600 text-white text-xs font-medium;
}
/* animasi popover */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
